import {useContext, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useProject} from "../context/ProjectContext";
import {AuthContext} from "../context/AuthProvider";
import {useTheme} from "../context/ThemeContext";
import {Sun, Moon, Bell, LogOut, ChevronDown, FolderKanban} from "lucide-react";
import {IconButton} from "./ui/IconButton";
import {NotificationsDropdown, NotificationItem} from "./NotificationsDropdown";

export const NavBar = () => {
    const location = useLocation();
    const isForms = location.pathname === '/forms';
    const {logout} = useContext(AuthContext);
    const {selectedProjectId, projects, setSelectedProjectId} = useProject();
    const {theme, toggleTheme} = useTheme();
    const isDarkMode = theme === "dark";
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const notificationsRef = useRef<HTMLDivElement>(null);
    const notifications: NotificationItem[] = [];

    useEffect(() => {
        if (!isNotificationsOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
                setIsNotificationsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isNotificationsOpen]);

    const handleLogout = () => {
        logout();
    };

    return (
        <nav
            className="min-h-16 w-full justify-between rounded-2xl bg-surface border border-border shrink-0 flex flex-row items-center px-5 py-2.5 gap-4 shadow-xs">
            <div>
                {isForms ? (
                    <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-[11px] font-semibold tracking-wide text-ink-faint uppercase pl-0.5">
                            <FolderKanban size={11}/>
                            Project
                        </span>
                        <div className="relative">
                            <select
                                className="appearance-none w-64 h-9 bg-surface-sunken rounded-lg pl-3 pr-8 text-sm font-medium text-ink outline-none focus:ring-4 focus:ring-primary-tint transition-colors"
                                value={selectedProjectId}
                                onChange={(e) => setSelectedProjectId(e.target.value)}
                            >
                                {projects.map(project => (
                                    <option key={project._id} value={project._id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-faint"/>
                        </div>
                    </div>
                ) : (
                    <div></div>
                )}
            </div>

            <div className="flex items-center flex-row gap-1">
                <IconButton
                    icon={isDarkMode ? Sun : Moon}
                    label="Toggle dark mode"
                    onClick={toggleTheme}
                />
                <div className="relative" ref={notificationsRef}>
                    <IconButton
                        icon={Bell}
                        label="Notifications"
                        active={isNotificationsOpen}
                        onClick={() => setIsNotificationsOpen(prev => !prev)}
                    />
                    {isNotificationsOpen && (
                        <NotificationsDropdown notifications={notifications}/>
                    )}
                </div>
                <IconButton
                    icon={LogOut}
                    label="Log out"
                    tone="danger"
                    onClick={handleLogout}
                />
            </div>
        </nav>
    );
};
